/**
 * Sequences the client-generated activities for a lesson (MCQ, MCQ-reverse,
 * Match, Write, Fill) one at a time using the SHARED activity kit
 * (`@/activities` `<ActivityRenderer variant="feed">`). The kit already
 * implements iOS-parity behavior: retry-until-correct (wrong → flash + reset,
 * no answer reveal), MCQ-reverse ("How do you say this?"), the parrot speech
 * bubble, and correct → auto-advance with NO Continue button (it holds the
 * green flash ~800ms then fires `onFinish`).
 *
 * This carousel only owns sequencing: each activity calls `onFinish` (the kit's
 * "done correctly" signal) → we report completion, play the correct sound, and
 * advance to the next activity; after the last one we call `onContinue` (which
 * steps the feed to the next video). Skipping moves on with no completion
 * reported. Advancing is button/auto-driven (not swipe) so it never fights the
 * outer horizontal lesson carousel's gestures.
 */
import { useState } from "react";
import { observer } from "@legendapp/state/react";
import { ChevronDown } from "lucide-react";
import { callWithAuth, postActivityComplete } from "@/api/Api";
import { ActivityRenderer, type ActivityType } from "@/activities";
import { useSound } from "@/hooks/useSound";
import { completedActivities$, generatedActivities$ } from "../state/videoState";
import { VIDEO_MAX_W } from "./videoConstants";
import { TopBar, TOP_BAR_HEIGHT } from "../components/ui/TopBar";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";

interface FeedActivityCarouselProps {
  chapterId: number;
  lessonId: number;
  isActive: boolean;
  onContinue: () => void;
}

const CONTINUE_BTN =
  "flex items-center gap-2 px-7 py-3 rounded-full bg-white text-black text-sm font-semibold active:scale-95 transition-transform";

const FeedActivityCarousel = observer(function FeedActivityCarousel({
  chapterId,
  lessonId,
  isActive,
  onContinue,
}: FeedActivityCarouselProps) {
  const stored = generatedActivities$[lessonId]?.get();
  const activities = stored ?? [];
  const [index, setIndex] = useState(0);
  // Reset the step when the activity list is REPLACED for this lesson (e.g. the
  // 90% regen swaps in a fresh list) so a stale index can't point past the new
  // list or surface the "next video" affordance before the new quiz is done.
  // Adjust-during-render (no effect); compare the stored ref, not the `?? []`
  // fallback (which would be a new array every render → infinite reset).
  const [seenList, setSeenList] = useState(stored);
  // Bumped on every list replacement so ActivityRenderer remounts on regen even
  // when the index lands back on 0 (keying by index alone reuses the instance →
  // stale answer/animation state).
  const [regen, setRegen] = useState(0);
  if (seenList !== stored) {
    setSeenList(stored);
    setIndex(0);
    setRegen((r) => r + 1);
    // New quiz for this lesson → clear the per-type completion set so same-type
    // activities in the fresh list re-fire their completion event (the backend
    // already dedupes droplet awards, but the local guard would otherwise
    // suppress the event for a freshly-generated activity).
    completedActivities$[lessonId].set(new Set<string>());
  }
  const { playCorrectSound } = useSound();
  const { trackFeedCarouselSwiped, trackActivityCompleted, trackActivityAttempt } =
    useTrackFeedEvents();

  if (!isActive) return <div className="h-full w-full bg-black" />;

  const activity = activities[index];

  const goNext = () => {
    if (index < activities.length - 1) {
      const nextActivity = activities[index + 1];
      setIndex(index + 1);
      // iOS fires feed_carousel_swiped on every carousel index change with the
      // content type of the panel being moved to (here always "activity").
      trackFeedCarouselSwiped(
        chapterId,
        lessonId,
        "activity",
        nextActivity?.type,
        true, // auto-advance after a correct answer / skip
      );
    } else {
      onContinue(); // last activity → next video
    }
  };

  // Finished correctly (kit's onFinish; the kit already held the green flash):
  // report completion, play the correct sound, then advance.
  const handleFinish = (playSound?: boolean) => {
    if (activity) {
      // Skip re-awarding drops + re-firing analytics for an already-completed
      // activity type (iOS useFeedActivityCompletion early-returns when the type
      // is in completedActivities).
      const done = completedActivities$[lessonId].peek() ?? new Set<string>();
      if (!done.has(activity.type)) {
        completedActivities$[lessonId].set(new Set(done).add(activity.type));
        callWithAuth(postActivityComplete, {
          lessonId,
          // Report the raw activity type (iOS + the web lesson path both do).
          // The backend dedupes droplet awards per (lesson, user, activityType),
          // so collapsing mcq + mcq-reverse to one string here under-awarded a
          // droplet vs iOS (which counts them as two distinct activities).
          activityType: activity.type,
        }).catch(() => {
          /* fire-and-forget */
        });
        // Per-type event name (e.g. mcq_activity_completed) — matches iOS.
        trackActivityCompleted(activity.type, lessonId, chapterId);
      }
    }
    if (playSound !== false) playCorrectSound();
    goNext();
  };

  // attemptNumber is owned by the activity component (per-activity counter);
  // forward it so the feed attempt event carries attempt_number /
  // is_first_attempt (iOS parity).
  const handleTrackAttempt = (
    type: ActivityType,
    correct: boolean,
    attemptNumber: number,
  ) => {
    trackActivityAttempt(type, correct, lessonId, chapterId, attemptNumber);
  };

  // Skipped (not answered): just move on, no completion reported.
  const skip = () => goNext();

  return (
    <div className="h-full w-full flex flex-col items-center bg-black">
      <div className="fixed top-0 left-0 right-0 z-40 flex justify-center pointer-events-none">
        <div className={`w-full ${VIDEO_MAX_W} pointer-events-auto`}>
          <TopBar />
        </div>
      </div>
      <div className="shrink-0" style={{ height: TOP_BAR_HEIGHT }} />

      <div className={`w-full flex-1 min-h-0 flex flex-col items-center px-5 pb-24 ${VIDEO_MAX_W}`}>
        {/* Progress dots + Skip — advance past this activity without answering
            (iOS lets you swipe past). */}
        <div className="w-full flex items-center justify-between pt-2">
          {activities.length > 1 ? (
            <div className="flex items-center gap-1.5">
              {activities.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === index ? "w-5 bg-white" : "w-1.5 bg-white/30"
                  }`}
                />
              ))}
            </div>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={skip}
            className="text-white/50 hover:text-white/80 text-sm font-medium px-2 py-1 transition-colors"
          >
            {index < activities.length - 1 ? "Skip ›" : "Skip to video ›"}
          </button>
        </div>
        <div className="pb-4" />

        <div className="flex-1 w-full flex flex-col items-center justify-center">
          {activity ? (
            <ActivityRenderer
              key={`${regen}-${index}`}
              activity={activity}
              variant="feed"
              onFinish={handleFinish}
              onTrackAttempt={handleTrackAttempt}
              onSkip={skip}
            />
          ) : (
            <button type="button" onClick={onContinue} className={CONTINUE_BTN}>
              Next video <ChevronDown className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export { FeedActivityCarousel };
